import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useUser } from '@auth_module/hooks/useUser';
import { useNotification } from '@insights_module/hooks/useNotification';
import { fetchUserAvatar, fetchAvatars, updateUserAvatar } from '@auth_module/services';
import { QUERY_KEYS } from '@constants/queryKeys';
import defaultAvatarThumbnail from '@assets/images/default-avatar.svg';

const _defaultAvatar = {
  id: 1,
  name: 'becky',
  url: defaultAvatarThumbnail,
};

export interface IAvatar {
  id: number;
  name: string;
  url: string;
}

interface IuseAvatar {
  userAvatar: IAvatar;
  isLoadingUserAvatar: boolean;
  avatars: IAvatar[] | undefined;
  isLoadingAvatars: boolean;
  updateAvatar: (avatarId: number) => void;
  isUpdateSuccess: boolean;
  isUpdateLoading: boolean;
}

export const useAvatar = (): IuseAvatar => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notify, NTFC_TYPE } = useNotification();
  const { user } = useUser();

  const { data: userAvatar, isLoading: isLoadingUserAvatar } = useQuery({
    queryKey: [QUERY_KEYS.USER_AVATAR, { id: user?.id }],
    queryFn: () => fetchUserAvatar(user?.id),
    enabled: !!user?.id,
    staleTime: 60 * 60 * 1000, // 60 minutes
  });

  const { data: avatars, isLoading: isLoadingAvatars } = useQuery({
    queryKey: [QUERY_KEYS.AVATARS],
    queryFn: () => fetchAvatars(),
    staleTime: 60 * 60 * 1000, // 60 minutes
  });

  const {
    mutateAsync: updateAvatar,
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
  } = useMutation({
    mutationFn: async (avatarId: number) =>
      user?.id && (await updateUserAvatar({ user_id: user?.id, avatar_id: avatarId })),
    onSuccess: () => {
      queryClient.refetchQueries([QUERY_KEYS.USER_AVATAR]);
      notify({ message: t('avatar.update_success_message'), type: NTFC_TYPE.SUCCESS });
    },
    onError: (error: Error) =>
      notify({
        message: `${t('avatar.update_error_message', { message: error?.message })}`,
        type: NTFC_TYPE.DANGER,
      }),
  });

  return useMemo(
    () => ({
      userAvatar: userAvatar?.data || _defaultAvatar,
      avatars: avatars?.data,
      isLoadingUserAvatar,
      isLoadingAvatars,
      isUpdateLoading,
      isUpdateSuccess,
      updateAvatar,
    }),
    [
      userAvatar,
      avatars,
      isLoadingUserAvatar,
      isLoadingAvatars,
      isUpdateLoading,
      isUpdateSuccess,
      updateAvatar,
    ]
  );
};
